import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/api/client';
import { BOARD_BY_ID, BOARD_BY_SLUG } from '@/constants/boards';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { CharCount } from '@/components/atoms/CharCount';
import { Skeleton } from '@/components/atoms/Skeleton';
import { RichTextEditor } from '@/components/organisms/RichTextEditor';
import { DatePicker } from '@/components/atoms/DatePicker';
import { LIMITS } from '@/constants/limits';
import { POINT_POLICY } from '@/constants/points';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { usePointStore } from '@/store/pointStore';
import { toast } from '@/store/toastStore';
import { queryKeys } from '@/api/queryKeys';

interface PostForEdit {
  id: number;
  board_id: number;
  title: string;
  content: string;
  post_type: string;
  ticker: string | null;
  user_id: string;
  category_id: number | null;
  event_id: number | null;
}

/** 이벤트 수정 로드용 — GET /events/:id 응답 */
interface EventForEdit {
  id: number;
  starts_at: string;
  ends_at: string;
  reward_instant: string | null;
  reward_winner: string | null;
  how_to_join: string | null;
  caution: string | null;
  announce_date: string | null;
  instant_point: number;
  winner_point: number;
  action_type: string | null;
  action_url: string | null;
  winner_criteria: string | null;
  winner_count: number;
  is_perpetual: boolean;
  tier_rewards: Array<{
    rank: string;
    prize_type: 'point' | 'gift';
    point_amount?: number;
    gift_text?: string;
  }> | null;
}

interface BoardCategory {
  id: number;
  board_id: number;
  name: string;
  sort_order: number;
}

const NOTICE_OPS_BOARD_ID = 12;
const EVENT_BOARD_ID = 14;
/** 관리자 전용 게시판 (운영/공지 + 이벤트) */
const ADMIN_ONLY_BOARD_IDS = [NOTICE_OPS_BOARD_ID, EVENT_BOARD_ID];

/** 빈 에디터 상태(<p></p>) 등을 실내용으로 간주하지 않기 위한 텍스트 길이 기준 */
function plainTextLength(html: string): number {
  if (typeof document === 'undefined') return html.length;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent ?? '').trim().length;
}

export function WritePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoggedIn, me } = useAuthStore();
  /* setLoginModalOpen 은 UnauthorizedRedirect 내부에서 별도 구독 (본 컴포넌트는 불필요) */
  const addPointToast = usePointStore((s) => s.addToQueue);
  const [search] = useSearchParams();

  const initialBoard = search.get('board');
  const editIdRaw = search.get('edit');
  const editId = editIdRaw ? Number(editIdRaw) : null;
  const isEditMode = editId !== null && !Number.isNaN(editId);

  const [boardId, setBoardId] = useState<number>(
    initialBoard ? BOARD_BY_SLUG[initialBoard]?.id ?? 11 : 11,
  );
  const currentBoardName = BOARD_BY_ID[boardId]?.name ?? '자유게시판';
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [isPinned, setIsPinned] = useState(false);
  /** 고정 위치 (1부터). '' 이면 말단에 자동 부여 */
  const [pinOrder, setPinOrder] = useState<number | ''>('');
  /** 익명 작성 (건의/제안 13 전용) */
  const [isAnonymous, setIsAnonymous] = useState(false);
  /** 비공개 작성 (건의/제안 13 전용) — 작성자 + admin 만 조회 가능 */
  const [isPrivate, setIsPrivate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  /* 이벤트 폼 전용 상태 (이벤트 게시판 14) */
  const today = new Date().toISOString().slice(0, 10);
  const [evStartsAt, setEvStartsAt] = useState(today);
  const [evEndsAt, setEvEndsAt] = useState(today);
  /* reward_instant 는 참여 포인트(evInstantPoint) 로 일원화되어 UI 에서 제거됨.
     과거 데이터 호환을 위해 빈 값으로 유지 (서버엔 전송 안 됨). */
  const [evRewardInstant] = useState('');
  /* evRewardWinner 는 tier_rewards 의 첫 tier gift_text 로 치환 — 더 이상 state 로 보관 안 함 */
  const [evHowToJoin, setEvHowToJoin] = useState('');
  const [evCaution, setEvCaution] = useState('');
  const [evAnnounceDate, setEvAnnounceDate] = useState('');
  const [evInstantPoint, setEvInstantPoint] = useState<number>(0);
  /** 참여 포인트 지급 여부 — 체크 off 면 instant_point=0 전송 */
  const [evHasInstantPoint, setEvHasInstantPoint] = useState<boolean>(true);
  /* evWinnerPoint 도 tier_rewards 의 첫 tier point_amount 로 치환 — state 불필요 */
  /** 참여 버튼 설정 — action_type 에 매핑. 'none' 이면 버튼 숨김 */
  const [evActionType, setEvActionType] = useState<
    | 'none'
    | 'general'
    | 'random'
    | 'checkin'
    | 'write_post'
    | 'virtual'
    | 'url'
  >('general');
  const [evActionUrl, setEvActionUrl] = useState('');
  /** 당첨 기준 */
  const [evWinnerCriteria, setEvWinnerCriteria] = useState<
    | 'random'
    | 'first_come'
    | 'top_likes'
    | 'top_1'
    | 'top_3'
    | 'top_10'
    | 'admin_pick'
    | 'none'
  >('admin_pick');
  const [evWinnerCount, setEvWinnerCount] = useState<number>(0);
  /** 상시 이벤트 (마감 없음) */
  const [evIsPerpetual, setEvIsPerpetual] = useState(false);

  /* 당첨 기준 → 참여 버튼 타입 자동 매핑 (admin 이 수동 변경 가능, random/none 은 잠금) */
  useEffect(() => {
    const autoActionType: typeof evActionType = (() => {
      switch (evWinnerCriteria) {
        case 'random':
          return 'random';
        case 'none':
          return 'none';
        case 'first_come':
        case 'top_likes':
        case 'top_1':
        case 'top_3':
        case 'top_10':
        case 'admin_pick':
        default:
          return 'general';
      }
    })();
    if (autoActionType !== evActionType) {
      setEvActionType(autoActionType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evWinnerCriteria]);
  /* (제거됨 2026-04-23) 당첨 보상 유형 select — tier_rewards 배열이 단일 소스로 대체.
     1 tier = 단일 보상, N tier = 등수별 보상. 각 tier 가 포인트/현물 개별 선택. */

  /** 등수별 보상 — top_1/top_3/top_10 criteria 에서 사용.
      각 항목: { rank, prize_type, point_amount, gift_text } */
  interface TierReward {
    rank: string;
    prize_type: 'point' | 'gift';
    point_amount: number;
    gift_text: string;
  }
  const [evTierRewards, setEvTierRewards] = useState<TierReward[]>([]);

  /* criteria 변경 시 tier_rewards 템플릿 자동 생성 — 모든 criteria 에서 tier 입력 통합.
     - top_1 → 1 tier (1등)
     - top_3 → 3 tier
     - top_10 → 4 tier (1/2/3/TOP 10)
     - none → tier 없음
     - 그 외 (random/first_come/top_likes/admin_pick) → 1 tier ("당첨자") */
  useEffect(() => {
    const makeRow = (rank: string): TierReward => ({
      rank,
      prize_type: 'point',
      point_amount: 0,
      gift_text: '',
    });
    if (evWinnerCriteria === 'top_1') {
      setEvTierRewards([makeRow('1등')]);
    } else if (evWinnerCriteria === 'top_3') {
      setEvTierRewards([makeRow('1등'), makeRow('2등'), makeRow('3등')]);
    } else if (evWinnerCriteria === 'top_10') {
      setEvTierRewards([
        makeRow('1등'),
        makeRow('2등'),
        makeRow('3등'),
        makeRow('TOP 10 (4~10등)'),
      ]);
    } else if (evWinnerCriteria === 'none') {
      setEvTierRewards([]);
    } else {
      setEvTierRewards([makeRow('당첨자')]);
    }
  }, [evWinnerCriteria]);

  const updateTier = (idx: number, patch: Partial<TierReward>) => {
    setEvTierRewards((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    );
  };

  /* 고정글 체크박스 노출 조건 — 운영/공지 + 관리자 + 새 글 작성(수정 모드 제외) */
  const canPin =
    boardId === NOTICE_OPS_BOARD_ID && !!me?.adminRole && !isEditMode;

  /* 카테고리는 모든 게시판에서 조회 시도 — 어드민에서 카테고리 추가 시 자동 노출.
     이벤트(14) 는 자체 폼이 별도 — 카테고리 드롭다운은 categories.length>0 일 때만 노출하므로 충돌 없음.
     건의/제안(13) 익명 토글 등 게시판별 특수 옵션은 그대로 유지. */
  const SUGGEST_BOARD_ID = 13;
  const { data: categoriesData } = useQuery({
    queryKey: ['boards', boardId, 'categories'],
    queryFn: () =>
      get<{ categories: BoardCategory[] }>(`/boards/${boardId}/categories`),
    enabled: boardId > 0,
    staleTime: 5 * 60_000,
  });
  const categories = categoriesData?.categories ?? [];
  const hasCategory = categories.length > 0;

  /* 이벤트 폼 — 이벤트 게시판(14) + 관리자. 신규 작성과 편집 모두 동일 폼 사용. */
  const isEventForm = boardId === EVENT_BOARD_ID && !!me?.adminRole;

  /* ── edit 모드: 기존 게시글 프리로드 ────────── */
  const editQuery = useQuery({
    queryKey: queryKeys.posts.detail(editId ?? -1),
    queryFn: () => get<PostForEdit>(`/posts/${editId}`),
    enabled: isEditMode,
  });

  /* 편집 대상이 이벤트 글(event_id 있음) 이면 연결된 이벤트 상세 조회 */
  const editEventId = editQuery.data?.event_id ?? null;
  const editEventQuery = useQuery({
    queryKey: ['events', editEventId ?? -1, 'for-edit'],
    queryFn: () => get<EventForEdit>(`/events/${editEventId}`),
    enabled: isEditMode && !!editEventId,
  });

  useEffect(() => {
    if (!isEditMode) return;
    const data = editQuery.data;
    if (!data || hydrated) return;
    /* 이벤트 글(event_id 존재)인데 연결 이벤트 데이터가 아직 도착 안 했으면 hydration 대기.
       → 먼저 hydrated=true 되어버리면 evIsPerpetual 등 이벤트 필드가 비채워짐 */
    if (data.event_id && !editEventQuery.data) return;

    // 소유권 확인 — 본인 글만 수정 가능. 관리자는 통과.
    if (
      me &&
      data.user_id &&
      me.userId !== data.user_id &&
      !me.adminRole
    ) {
      toast.error('본인 글만 수정할 수 있어요.');
      navigate(`/post/${data.id}`, { replace: true });
      return;
    }

    setBoardId(data.board_id);
    setTitle(data.title);
    setContent(data.content);
    setCategoryId(data.category_id ?? '');

    /* 이벤트 수정 모드 — event 데이터를 폼에 채움 */
    if (data.event_id && editEventQuery.data) {
      const e = editEventQuery.data;
      setEvStartsAt(e.starts_at ? e.starts_at.slice(0, 10) : today);
      setEvEndsAt(e.ends_at ? e.ends_at.slice(0, 10) : today);
      setEvHowToJoin(e.how_to_join ?? '');
      setEvCaution(e.caution ?? '');
      setEvAnnounceDate(e.announce_date ? e.announce_date.slice(0, 10) : '');
      setEvInstantPoint(e.instant_point ?? 0);
      setEvHasInstantPoint((e.instant_point ?? 0) > 0);
      setEvActionType(
        (e.action_type ?? 'general') as typeof evActionType,
      );
      setEvActionUrl(e.action_url ?? '');
      setEvWinnerCriteria(
        (e.winner_criteria ?? 'admin_pick') as typeof evWinnerCriteria,
      );
      setEvWinnerCount(e.winner_count ?? 0);
      setEvIsPerpetual(!!e.is_perpetual);
      if (Array.isArray(e.tier_rewards) && e.tier_rewards.length > 0) {
        setEvTierRewards(
          e.tier_rewards.map((t) => ({
            rank: t.rank,
            prize_type: t.prize_type,
            point_amount: t.point_amount ?? 0,
            gift_text: t.gift_text ?? '',
          })),
        );
      }
    }
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editQuery.data, editEventQuery.data, hydrated, me, navigate]);

  /*
   * 카테고리 기본값 — '기타'
   * — 새 글 작성: 카테고리 로드 시 '기타' 자동 선택
   * — 수정 모드: 기존 글에 카테고리가 없으면(category_id = null) '기타' 로 보정
   *   → hydrated 후 categoryId === '' 조건으로 판정
   */
  useEffect(() => {
    if (!hasCategory) return;
    if (categories.length === 0) return;
    if (categoryId !== '') return;
    if (isEditMode && !hydrated) return; // 편집 모드는 DB 값 로드 후에만 적용
    /* 게시판별 기본 카테고리:
       - 운영/공지(12) → '공지'
       - 그 외 (자유게시판/건의·제안) → '기타' */
    const preferredName = boardId === NOTICE_OPS_BOARD_ID ? '공지' : '기타';
    const preferred =
      categories.find((c) => c.name === preferredName) ??
      categories.find((c) => c.name === '기타');
    if (preferred) setCategoryId(preferred.id);
  }, [boardId, categories, categoryId, isEditMode, hydrated]);

  /* 고정글 체크 시 카테고리를 '공지' 로 자동 전환 — 사용자가 수동 변경은 허용(이펙트는 체크 순간 1회) */
  useEffect(() => {
    if (!isPinned) return;
    if (boardId !== NOTICE_OPS_BOARD_ID) return;
    const notice = categories.find((c) => c.name === '공지');
    if (notice && categoryId !== notice.id) {
      setCategoryId(notice.id);
    }
    /* categoryId 는 의존성에 넣지 않음 — 사용자가 수동 변경한 직후 재강제 하지 않음 */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPinned, boardId, categories]);

  /* ── 저장 mutation — edit 여부로 POST/PUT 분기.
     이벤트 게시판(14)은 응답에 eventId 가 동봉돼 와 /events/:id 로 리디렉션. */
  const mutation = useMutation({
    mutationFn: async (): Promise<{ id: number; eventId?: number | null }> => {
      /*
       * 관리자 전용 게시판(운영/공지 12, 이벤트 14) 은 관리자만 작성 가능.
       * 서버에서도 requireAdmin 체크하지만 UX 개선을 위해 선제 검증.
       */
      if (ADMIN_ONLY_BOARD_IDS.includes(boardId) && !me?.adminRole) {
        throw new Error('관리자만 작성할 수 있어요.');
      }

      /*
       * 카테고리(말머리) 처리
       * — 말머리가 있는 게시판(자유게시판 / 운영/공지)에서 사용자가 선택하지 않으면 '기타' 로 기본 적용
       * — '기타' 카테고리가 없으면 category_id 전송 생략
       */
      let effectiveCategoryId: number | null = null;
      if (hasCategory && categories.length > 0) {
        if (categoryId !== '') {
          effectiveCategoryId = Number(categoryId);
        } else {
          const etc = categories.find((c) => c.name === '기타');
          if (etc) effectiveCategoryId = etc.id;
        }
      }
      const payloadCategory =
        effectiveCategoryId != null ? { categoryId: effectiveCategoryId } : {};

      /* isPinned 는 운영/공지 + 관리자 조합에서만 서버에서 반영됨. 그 외 무시. */
      const payloadPinned =
        canPin && isPinned
          ? { isPinned: true, ...(pinOrder !== '' ? { pinOrder } : {}) }
          : {};

      /* 이벤트 폼 — 이벤트 게시판(14) 일 때만 event 필드 동봉 */
      /* tier_rewards 의 첫 항목을 legacy winner_point/reward_winner 로 미러링 —
         "포인트 안내" 블록 등 기존 컴포넌트 호환 유지. */
      const firstTier = evTierRewards[0];
      const legacyWinnerPoint =
        firstTier && firstTier.prize_type === 'point' ? firstTier.point_amount : 0;
      const legacyRewardWinner =
        firstTier && firstTier.prize_type === 'gift'
          ? firstTier.gift_text.trim()
          : '';

      const payloadEvent = isEventForm
        ? {
            event: {
              startsAt: evStartsAt ? `${evStartsAt}T00:00:00+09:00` : undefined,
              endsAt: evEndsAt ? `${evEndsAt}T23:59:59+09:00` : undefined,
              rewardInstant: evRewardInstant.trim() || undefined,
              rewardWinner: legacyRewardWinner || undefined,
              howToJoin: evHowToJoin.trim() || undefined,
              caution: evCaution.trim() || undefined,
              announceDate:
                evIsPerpetual ? undefined : evAnnounceDate || undefined,
              instantPoint:
                evHasInstantPoint && evInstantPoint > 0 ? evInstantPoint : undefined,
              winnerPoint: legacyWinnerPoint > 0 ? legacyWinnerPoint : undefined,
              actionType: evActionType,
              actionUrl:
                evActionType === 'url' && evActionUrl.trim()
                  ? evActionUrl.trim()
                  : undefined,
              winnerCriteria: evWinnerCriteria,
              winnerCount:
                evWinnerCriteria === 'first_come' && evWinnerCount > 0
                  ? evWinnerCount
                  : undefined,
              isPerpetual: evIsPerpetual,
              tierRewards:
                evTierRewards.length > 0
                  ? evTierRewards.map((t) => ({
                      rank: t.rank,
                      prize_type: t.prize_type,
                      point_amount:
                        t.prize_type === 'point' ? t.point_amount : undefined,
                      gift_text:
                        t.prize_type === 'gift' ? t.gift_text.trim() : undefined,
                    }))
                  : undefined,
            },
          }
        : {};

      /* 익명 — 건의/제안(13) + 체크 시만 전송. 서버가 board_id 재검증. */
      const payloadAnonymous =
        boardId === SUGGEST_BOARD_ID && isAnonymous ? { isAnonymous: true } : {};
      const payloadPrivate =
        boardId === SUGGEST_BOARD_ID && isPrivate ? { isPrivate: true } : {};

      /* 편집 모드 — PUT /posts/:id. 이벤트 글(board 14) 이면 event 블록도 전송. */
      if (isEditMode && editId) {
        await put(`/posts/${editId}`, {
          title,
          content,
          ...payloadCategory,
          ...payloadEvent,
        });
        return { id: editId };
      }

      return post<{ id: number; eventId?: number | null }>('/posts', {
        boardId,
        title,
        content,
        ...payloadCategory,
        ...payloadPinned,
        ...payloadEvent,
        ...payloadAnonymous,
        ...payloadPrivate,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.detail(data.id) });
      // 새 글 작성(수정 제외)일 때만 포인트 반영 + 토스트
      if (!isEditMode) {
        queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
        queryClient.invalidateQueries({ queryKey: queryKeys.point.me });
        queryClient.invalidateQueries({ queryKey: ['point', 'history'] });
        /* 토스트 금액은 실제 정책값과 일치 — 일반 20P / 프리미엄 30P.
           포인트는 서버에서 본문 텍스트 20자 이상일 때만 지급되므로,
           20자 미만(이미지만 등) 글은 토스트도 띄우지 않음. */
        if (plainTextLength(content) >= 20) {
          const earned = me?.isPremium
            ? POINT_POLICY.write_post_premium
            : POINT_POLICY.write_post;
          addPointToast(earned, '게시글 작성');
        }
      }
      /* 이벤트 글(board 14)은 eventId 가 함께 오므로 EventDetailPage 로 이동,
         그 외 일반 글은 PostDetailPage 로 이동. */
      /* 신규 작성은 data.eventId 동봉, 편집은 기존 editEventId 사용 */
      const targetEventId = data.eventId ?? editEventId ?? null;
      if (targetEventId) {
        navigate(`/events/${targetEventId}`, { replace: true });
      } else {
        navigate(`/post/${data.id}`, { replace: true });
      }
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.error?.message ??
          err?.message ??
          '저장에 실패했어요.',
      );
    },
  });

  /* 비로그인 안전망 — render 도중 setState 호출 + return null 패턴은
     React anti-pattern (모달 미노출 + 빈 화면). useEffect 로 분리해 다음 tick 에
     모달 띄우고 history back. BoardPage 글쓰기 버튼에서 1차 차단되지만,
     URL 직접 진입 / 세션 만료 케이스를 위해 유지. */
  if (!isLoggedIn) {
    return <UnauthorizedRedirect />;
  }

  if (isEditMode && editQuery.isLoading) {
    return (
      <div className="container-base py-6">
        <Skeleton className="h-10 w-2/3 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const textLen = plainTextLength(content);
  const htmlLen = content.length;
  /* 이미지 첨부 여부 — 본문에 <img> 가 있으면 텍스트가 없어도 발행 허용 (2026-05).
     포인트 지급은 서버에서 textLen >= 20 일 때만 — 이미지만 있는 글은 미지급. */
  const hasImage = /<img[\s/>]/i.test(content);
  const canSubmit =
    !uploading &&
    title.length >= LIMITS.post.titleMin &&
    title.length <= LIMITS.post.titleMax &&
    (textLen >= LIMITS.post.contentMin || hasImage) &&
    htmlLen <= LIMITS.post.contentMax;

  return (
    <div className="container-base py-6">
      {/* 타이틀 — 게시판명으로 통일. 수정 모드는 "게시판명 · 글 수정" 로 표시.
          별도 '게시판' 라벨/chip 은 제거(타이틀이 역할 대체). */}
      <h1 className="mb-6 text-2xl font-extrabold">
        {currentBoardName}
        {isEditMode && (
          <span className="ml-2 text-base font-medium text-[var(--color-text-sub)]">
            · 글 수정
          </span>
        )}
      </h1>

      {/* 카테고리 드롭다운 — 자유게시판(11) 전용, 선택사항 */}
      {hasCategory && categories.length > 0 && (
        <div className="mb-4">
          <label
            htmlFor="post-category"
            className="mb-1.5 block text-sm font-medium"
          >
            카테고리
          </label>
          <select
            id="post-category"
            value={categoryId}
            onChange={(e) =>
              setCategoryId(e.target.value === '' ? '' : Number(e.target.value))
            }
            className="h-10 w-full max-w-xs rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
          >
            {/* '기타' 가 없는 게시판에서만 빈 옵션 제공 — 있는 경우 '기타' 가 기본값 */}
            {!categories.some((c) => c.name === '기타') && (
              <option value="">카테고리 없음</option>
            )}
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 고정글 체크박스 — 운영/공지(12) + 관리자만 노출 */}
      {canPin && (
        <div className="mb-4 space-y-2">
          <label className="mb-1.5 block text-sm font-medium">옵션</label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={isPinned}
              onChange={(e) => {
                setIsPinned(e.target.checked);
                if (!e.target.checked) setPinOrder('');
              }}
              className="h-4 w-4 rounded accent-[var(--color-primary)]"
            />
            <span className="font-medium">게시판 상단에 고정</span>
            <span className="text-xs text-[var(--color-text-sub)]">
              · 최대 5개, 설정한 순서대로 상단 노출
            </span>
          </label>
          {isPinned && <PinOrderSelect value={pinOrder} onChange={setPinOrder} />}
        </div>
      )}

      {/* 익명/비공개 작성 — 건의/제안(13) 전용 */}
      {boardId === SUGGEST_BOARD_ID && !isEditMode && (
        <div className="mb-4 space-y-2">
          <label className="mb-1.5 block text-sm font-medium">옵션</label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="h-4 w-4 rounded accent-[var(--color-primary)]"
            />
            <span className="font-medium">익명으로 작성</span>
            <span className="text-xs text-[var(--color-text-sub)]">
              · 작성자 닉네임이 "익명" 으로 표시돼요
            </span>
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="h-4 w-4 rounded accent-[var(--color-primary)]"
            />
            <span className="font-medium">숨긴 글로 작성</span>
            <span className="text-xs text-[var(--color-text-sub)]">
              · 본인과 운영자만 볼 수 있어요
            </span>
          </label>
        </div>
      )}

      {/* 제목 */}
      <div className="mb-4">
        <label className="mb-1.5 block text-sm font-medium">제목</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="제목을 입력하세요"
          maxLength={LIMITS.post.titleMax}
        />
        <div className="mt-1 text-right">
          <CharCount current={title.length} max={LIMITS.post.titleMax} />
        </div>
      </div>

      {/* 이벤트 전용 폼 — 운영/공지 + 카테고리='이벤트' 일 때만 노출.
          일반 본문 에디터는 "이벤트 소개" 자리로 재활용(하단) 해 1개만 유지. */}
      {isEventForm && (
        <div className="mb-6 space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <p className="text-sm font-semibold text-[var(--color-text)]">이벤트 상세</p>
          {/* 기간 + 상시 체크 */}
          <div className="space-y-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={evIsPerpetual}
                onChange={(e) => setEvIsPerpetual(e.target.checked)}
                className="h-4 w-4 rounded accent-[var(--color-primary)]"
              />
              <span className="font-medium">상시 이벤트 (마감 없음)</span>
              <span className="text-xs text-[var(--color-text-sub)]">
                · 출석체크·첫 게시글 등 마감 없는 이벤트
              </span>
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium">시작일</label>
                <DatePicker
                  value={evStartsAt}
                  onChange={(v) => {
                    setEvStartsAt(v);
                    if (v && evEndsAt && v > evEndsAt) setEvEndsAt(v);
                  }}
                  min={today}
                  ariaLabel="이벤트 시작일"
                />
                <p className="mt-1 text-[11px] text-[var(--color-text-sub)]">
                  오늘({today}) 이후만 선택 가능해요.
                </p>
              </div>
              {!evIsPerpetual && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium">종료일</label>
                  <DatePicker
                    value={evEndsAt}
                    onChange={setEvEndsAt}
                    min={evStartsAt || today}
                    ariaLabel="이벤트 종료일"
                  />
                </div>
              )}
            </div>
          </div>
          {/* 참여 포인트 — 고정 지급, 지급 여부 토글 */}
          <div className="space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={evHasInstantPoint}
                onChange={(e) => setEvHasInstantPoint(e.target.checked)}
                className="h-4 w-4 rounded accent-[var(--color-primary)]"
              />
              <span className="font-medium">참여 포인트 지급</span>
              <span className="text-xs text-[var(--color-text-sub)]">
                · 모든 참여자에게 고정 지급
              </span>
            </label>
            {evHasInstantPoint && (
              <Input
                type="number"
                min={0}
                max={100000}
                value={evInstantPoint}
                onChange={(e) => setEvInstantPoint(Number(e.target.value) || 0)}
                placeholder="지급할 포인트 (예: 10)"
                className="max-w-xs"
              />
            )}
          </div>

          {/* 당첨 기준 (보상 입력은 기준별 tier 블록에서 처리) */}
          <div className="space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
            <label className="block text-sm font-medium">당첨 기준</label>
            <select
              value={evWinnerCriteria}
              onChange={(e) =>
                setEvWinnerCriteria(e.target.value as typeof evWinnerCriteria)
              }
              className="h-10 w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
            >
              <option value="admin_pick">관리자 지정</option>
              <option value="random">랜덤 추첨</option>
              <option value="first_come">선착순</option>
              <option value="top_likes">추천순</option>
              <option value="top_1">1등 (단독 당첨)</option>
              <option value="top_3">TOP 3 (1·2·3등)</option>
              <option value="top_10">TOP 10 (상위 10명)</option>
              <option value="none">당첨자 없음</option>
            </select>
            {evWinnerCriteria === 'first_come' && (
              <div>
                <label className="mb-1.5 mt-2 block text-sm font-medium">모집 인원</label>
                <Input
                  type="number"
                  min={0}
                  max={10000}
                  value={evWinnerCount}
                  onChange={(e) => setEvWinnerCount(Number(e.target.value) || 0)}
                  placeholder="예: 100"
                  className="max-w-xs"
                />
                <p className="mt-1 text-[11px] text-[var(--color-text-sub)]">
                  선착순 모집 인원. 참여 시점 순으로 관리자가 지정하세요.
                </p>
              </div>
            )}

            {/* 등수별 보상 입력 — top_1/3/10 criteria 에서 동적 */}
            {evTierRewards.length > 0 && (
              <div className="mt-3 space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
                <label className="block text-sm font-medium">등수별 보상</label>
                {evTierRewards.map((t, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[72px_1fr] gap-2 sm:grid-cols-[88px_120px_1fr] sm:items-center"
                  >
                    <div className="text-sm font-semibold text-[var(--color-text)]">
                      {t.rank}
                    </div>
                    <select
                      value={t.prize_type}
                      onChange={(e) =>
                        updateTier(i, {
                          prize_type: e.target.value as 'point' | 'gift',
                        })
                      }
                      className="h-9 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
                    >
                      <option value="point">포인트</option>
                      <option value="gift">현물 (선물)</option>
                    </select>
                    {t.prize_type === 'point' ? (
                      <Input
                        type="number"
                        min={0}
                        max={1000000}
                        value={t.point_amount}
                        onChange={(e) =>
                          updateTier(i, { point_amount: Number(e.target.value) || 0 })
                        }
                        placeholder="0"
                      />
                    ) : (
                      <Input
                        value={t.gift_text}
                        onChange={(e) => updateTier(i, { gift_text: e.target.value })}
                        placeholder="예: 스타벅스 아메리카노 기프티콘"
                        maxLength={500}
                      />
                    )}
                  </div>
                ))}
                <p className="text-[11px] text-[var(--color-text-sub)]">
                  각 등수마다 포인트 또는 현물을 선택해 개별 입력하세요.
                </p>
              </div>
            )}
            <p className="text-[11px] text-[var(--color-text-sub)]">
              {evWinnerCriteria === 'admin_pick'
                ? '관리자가 직접 지정하여 당첨자를 등록합니다.'
                : evWinnerCriteria === 'random'
                ? '참여자 중 무작위 추첨 (관리자가 추첨 결과를 당첨자로 등록).'
                : evWinnerCriteria === 'first_come'
                ? '참여 시각 순으로 선착 모집. 정원 도달 시 조기 마감 가능.'
                : evWinnerCriteria === 'top_likes'
                ? '댓글/추천수 상위 유저를 당첨자로 선정.'
                : evWinnerCriteria === 'top_1'
                ? '가장 기여도 높은 1명만 단독 당첨.'
                : evWinnerCriteria === 'top_3'
                ? '1·2·3등 3명 선정. 등수별 다른 보상을 "당첨 선물" 에 명시하세요.'
                : evWinnerCriteria === 'top_10'
                ? '상위 10명 일괄 당첨.'
                : '당첨 절차 없이 참여 포인트만 지급하는 이벤트.'}
            </p>
          </div>
          {/* 참여 방법 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">참여 방법</label>
            <Input
              value={evHowToJoin}
              onChange={(e) => setEvHowToJoin(e.target.value)}
              placeholder="예: 이 글 하단에 댓글로 참여"
              maxLength={1000}
            />
          </div>
          {/* 주의사항 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">주의사항</label>
            <Input
              value={evCaution}
              onChange={(e) => setEvCaution(e.target.value)}
              placeholder="예: 어뷰징 계정 제외, 1인 1회"
              maxLength={1000}
            />
          </div>
          {/* 발표일 — 상시 이벤트는 발표 개념이 없어 숨김 */}
          {!evIsPerpetual && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">발표일</label>
              <DatePicker
                value={evAnnounceDate}
                onChange={setEvAnnounceDate}
                min={evEndsAt || evStartsAt || today}
                placeholder="발표일 선택 (선택)"
                ariaLabel="이벤트 발표일"
              />
            </div>
          )}

          {/* 참여 버튼 설정 — 버튼 노출 여부 + 이동 경로 */}
          <div className="space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
            <label className="block text-sm font-medium">참여 버튼</label>
            <select
              value={evActionType}
              onChange={(e) => setEvActionType(e.target.value as typeof evActionType)}
              disabled={evWinnerCriteria === 'random' || evWinnerCriteria === 'none'}
              className="h-10 w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <option value="none">버튼 숨김 (참여 불필요)</option>
              <option value="general">일반 참여 (참여 기록만)</option>
              <option value="random">랜덤 추첨 응모</option>
              <option value="checkin">출석체크 페이지로 이동</option>
              <option value="write_post">글쓰기 페이지로 이동</option>
              <option value="virtual">모의투자 페이지로 이동</option>
              <option value="url">커스텀 URL 이동</option>
            </select>
            {(evWinnerCriteria === 'random' || evWinnerCriteria === 'none') && (
              <p className="text-[11px] text-[var(--color-text-sub)]">
                {evWinnerCriteria === 'random'
                  ? '당첨 기준이 "랜덤 추첨" 이라 참여 버튼은 "랜덤 추첨 응모" 로 고정돼요.'
                  : '당첨 기준이 "당첨자 없음" 이라 참여 버튼은 숨김 처리돼요.'}
              </p>
            )}
            {evActionType === 'url' && (
              <>
                <Input
                  value={evActionUrl}
                  onChange={(e) => setEvActionUrl(e.target.value)}
                  placeholder="https://forms.example.com/... 또는 /some-path"
                  maxLength={500}
                />
                <p className="text-[11px] text-[var(--color-text-sub)]">
                  http(s):// 로 시작하면 새 탭에서, 아니면 사이트 내부 경로로 이동합니다.
                </p>
              </>
            )}
            <p className="text-[11px] text-[var(--color-text-sub)]">
              {evActionType === 'none'
                ? '참여 버튼이 노출되지 않습니다. (공지성 이벤트 등)'
                : evActionType === 'general'
                ? '"참여하기" 버튼을 눌러 참여 기록만 남깁니다.'
                : evActionType === 'random'
                ? '"랜덤 추첨 응모" 버튼 — 참여자를 풀에 추가해 관리자가 추첨합니다.'
                : evActionType === 'url'
                ? '지정한 URL 로 이동합니다. 외부 폼/랜딩 등에 유용합니다.'
                : '해당 기능 페이지로 이동합니다.'}
            </p>
          </div>
        </div>
      )}

      {/* 본문 — 리치 텍스트 에디터 (이벤트 폼이면 '이벤트 소개' 로 라벨 전환) */}
      <div className="mb-6">
        <label className="mb-1.5 block text-sm font-medium">
          {isEventForm ? '이벤트 소개' : '내용'}
        </label>
        <RichTextEditor
          value={content}
          onChange={setContent}
          placeholder={isEventForm ? '이벤트 배경/안내를 10자 이상 입력하세요' : '내용을 10자 이상 입력하세요'}
          maxLength={LIMITS.post.contentMax}
          onUploadingChange={setUploading}
        />
        <div className="mt-1 flex justify-between text-xs">
          <span
            className={
              htmlLen > LIMITS.post.contentMax
                ? 'text-[var(--color-rise)]'
                : 'text-[var(--color-text-sub)]'
            }
          >
            {htmlLen > LIMITS.post.contentMax
              ? `본문이 너무 길어요 (${LIMITS.post.contentMax.toLocaleString()}자 초과)`
              : textLen < LIMITS.post.contentMin && !hasImage
              ? '내용을 10자 이상 입력하거나 이미지를 첨부해 주세요'
              : ''}
          </span>
          <CharCount current={htmlLen} max={LIMITS.post.contentMax} />
        </div>
        {/* 어뷰징 방지 — 포인트 지급 최소 글자수 안내 (2026-05) */}
        <div className="mt-1 text-[11px] text-[var(--color-text-sub)]">
          {textLen < 20
            ? `20자 이상 작성 시 포인트가 지급됩니다 (현재 ${textLen}자)`
            : '✓ 포인트 지급 조건 충족'}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          취소
        </Button>
        <Button
          disabled={!canSubmit}
          loading={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {isEditMode ? '수정 완료' : '등록'}
        </Button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
 *  UnauthorizedRedirect — 비로그인 진입 시 로그인 모달 + history back.
 *  render 외부(useEffect)에서 상태 변경하여 React 경고 회피.
 * ══════════════════════════════════════════════════════════════ */
function UnauthorizedRedirect() {
  const navigate = useNavigate();
  const setLoginModalOpen = useUIStore((s) => s.setLoginModalOpen);
  useEffect(() => {
    setLoginModalOpen(true);
    /* 직전 페이지로 복귀 — 진입 경로(게시판/홈)에 모달이 떠 있는 상태 */
    navigate(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/* ══════════════════════════════════════════════════════════════
 *  PinOrderSelect — 고정 위치 드롭다운 (1..currentCount+1, max 5)
 *  현재 고정된 글 목록을 GET /posts/pinned 로 조회해 삽입 가능 위치 계산.
 * ══════════════════════════════════════════════════════════════ */
function PinOrderSelect({
  value,
  onChange,
}: {
  value: number | '';
  onChange: (v: number | '') => void;
}) {
  const pinnedQuery = useQuery({
    queryKey: ['posts', 'pinned'],
    queryFn: () => get<Array<{ id: number; title: string; pin_order: number }>>('/posts/pinned'),
    staleTime: 60_000,
  });
  const current = pinnedQuery.data ?? [];
  const maxPosition = Math.min(current.length + 1, 5);
  const positions = Array.from({ length: maxPosition }, (_, i) => i + 1);

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
      <label className="mb-1.5 block text-sm font-medium">고정 순서</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        className="h-10 w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
      >
        <option value="">자동 (마지막 순서로 추가)</option>
        {positions.map((p) => (
          <option key={p} value={p}>
            {p}번째
            {p <= current.length ? ` (기존: ${current[p - 1]?.title?.slice(0, 24) ?? ''}…)` : ''}
          </option>
        ))}
      </select>
      <p className="mt-1 text-[11px] text-[var(--color-text-sub)]">
        현재 고정 {current.length}개 / 최대 5개. 선택한 위치에 삽입되고 이후 글은 한 칸씩 밀립니다.
      </p>
    </div>
  );
}
